import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import { EmptyState, StatusChip, type StatusTone } from '@routes-plan/ui';
import { useT } from '../../../i18n';

export type MobileDevice = {
  id: number;
  installationId: string;
  status: string;
  deviceName?: string | null;
  platform?: string | null;
  brand?: string | null;
  model?: string | null;
  appVersion?: string | null;
  lastLoginAt?: string | null;
  lastSeenAt?: string | null;
};

export type MobileDeviceRequest = {
  id: number;
  installationId: string;
  status: string;
  deviceName?: string | null;
  platform?: string | null;
  appVersion?: string | null;
  requestReason?: string | null;
  reviewNote?: string | null;
  requestedAt?: string | null;
  reviewedAt?: string | null;
};

interface Props {
  devices: MobileDevice[];
  requests: MobileDeviceRequest[];
  onDeviceStatus: (deviceId: number, status: 'approved' | 'blocked') => void;
  onRequestStatus: (requestId: number, status: 'approved' | 'rejected') => void;
  /** Si es true, solo las solicitudes pendientes se pueden aprobar o rechazar. */
  onlyPendingRequests?: boolean;
  disabled?: boolean;
}

const Line: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
    {children}
  </Typography>
);

/** Dispositivos de la app de un representante y sus solicitudes de cambio de dispositivo. */
const MobileDevicesPanel: React.FC<Props> = ({ devices, requests, onDeviceStatus, onRequestStatus, onlyPendingRequests, disabled }) => {
  const t = useT();
  const na = t('salesReps.notAvailable');
  const deviceTone = (s: string): StatusTone => (s === 'approved' ? 'success' : s === 'blocked' ? 'danger' : 'warning');
  const requestTone = (s: string): StatusTone => (s === 'approved' ? 'success' : s === 'rejected' ? 'danger' : 'warning');

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          {t('salesReps.mobileDevicesTitle')}
        </Typography>
        {devices.length === 0 ? (
          <EmptyState dense icon={<PhoneAndroidOutlinedIcon />} title={t('salesReps.noDevices')} />
        ) : (
          <Stack spacing={1.5}>
            {devices.map((device) => (
              <Paper key={device.id} variant="outlined" sx={{ p: 2, minWidth: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ minWidth: 0, flex: '1 1 240px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                      <Typography variant="subtitle2">{device.deviceName || t('salesReps.unnamedDevice')}</Typography>
                      <StatusChip
                        label={
                          device.status === 'approved'
                            ? t('salesReps.statusApproved')
                            : device.status === 'blocked'
                              ? t('salesReps.statusBlocked')
                              : t('salesReps.statusPending')
                        }
                        tone={deviceTone(device.status)}
                      />
                    </Box>
                    <Line>{t('salesReps.installationId', { value: device.installationId })}</Line>
                    <Line>{t('salesReps.platformModel', { platform: device.platform || na, model: device.model || na })}</Line>
                    <Line>{t('salesReps.appLastLogin', { app: device.appVersion || na, lastLogin: device.lastLoginAt || na })}</Line>
                    {device.lastSeenAt && <Line>{t('salesReps.lastSeen', { value: device.lastSeenAt })}</Line>}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => onDeviceStatus(device.id, 'approved')}
                      disabled={disabled || device.status === 'approved'}
                    >
                      {t('salesReps.approve')}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => onDeviceStatus(device.id, 'blocked')}
                      disabled={disabled || device.status === 'blocked'}
                    >
                      {t('salesReps.block')}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          {t('salesReps.deviceRequestsTitle')}
        </Typography>
        {requests.length === 0 ? (
          <EmptyState dense title={t('salesReps.noDeviceRequests')} />
        ) : (
          <Stack spacing={1.5}>
            {requests.map((request) => {
              const locked = onlyPendingRequests ? request.status !== 'pending' : false;
              return (
                <Paper key={request.id} variant="outlined" sx={{ p: 2, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 0, flex: '1 1 240px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                        <Typography variant="subtitle2">{t('salesReps.requestNumber', { id: request.id })}</Typography>
                        <StatusChip
                          label={
                            request.status === 'approved'
                              ? t('salesReps.requestApproved')
                              : request.status === 'rejected'
                                ? t('salesReps.requestRejected')
                                : t('salesReps.requestPending')
                          }
                          tone={requestTone(request.status)}
                        />
                      </Box>
                      <Line>{t('salesReps.devicePlatform', { device: request.deviceName || na, platform: request.platform || na })}</Line>
                      <Line>{t('salesReps.installationId', { value: request.installationId })}</Line>
                      <Line>{t('salesReps.requestedAt', { value: request.requestedAt || na })}</Line>
                      <Line>{t('salesReps.requestReason', { value: request.requestReason || t('salesReps.noDetail') })}</Line>
                      {request.reviewNote && <Line>{t('salesReps.reviewNote', { value: request.reviewNote })}</Line>}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => onRequestStatus(request.id, 'approved')}
                        disabled={disabled || locked || request.status === 'approved'}
                      >
                        {t('salesReps.approve')}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => onRequestStatus(request.id, 'rejected')}
                        disabled={disabled || locked || request.status === 'rejected'}
                      >
                        {t('salesReps.reject')}
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>
    </Stack>
  );
};

export default MobileDevicesPanel;
